import {Injectable} from '@angular/core';
import {LabOrder} from '../shared/data/lab-order';
import {Coating} from '../shared/data/coating';
import {LensCatalogBusinessService} from '../shared/lens-catalog-business.service';
import {CoatingTypes} from '../shared/data/coatingTypes';
import {UtilsService} from '../shared/utils.service';
import {LensEnhancementDescription} from '../shared/data/lens-enhancement-description';
import {BillingOptionCode} from '../shared/data/billing-option-code';
import {Lens} from '../shared/data/lens';

@Injectable()
export class LabCodeMapperService {

  constructor(private lensCatalogBusinessService: LensCatalogBusinessService, private coatingTypes: CoatingTypes) {
  }

  initializeLabOrderMapping(result: LabOrder): LabOrder {
    if (result.lens.spectacleLensId) {
      this.getLensDescription(result);
    }

    result.frame.status = this.deduceFrameStatus(result.frame.supplier);
    return result;
  }

  // TODO: Added per LABPORTAL-293; should be replaced via LABPORTAL-280
  // Since we are not getting Frame.status from the api, we're mapping it in here. When 280 is done, this can be removed.
  deduceFrameStatus(supplier: string) {
    if (!supplier) {
      return '';
    }

    let retVal = '';
    switch (supplier) {
      case 'LENS_ONLY' :
        retVal = 'Lenses Only';
        break;
      case 'LAB' :
        retVal = 'Lab Supplied';
        break;
      case 'DOCTOR' :
        retVal = 'To Come';
        break;
      case 'PATIENT' :
        retVal = 'To Come';
        break;
      default:
        retVal = '';
    }
    return retVal;
  }

  // Build description used by view lab order.
  // Example: 'Lab Choice (AR Coating A) [A1, QM]'
  getCoatingDescription(type: string, coating: Coating): string {
    if (!coating) {
      return '';
    }
    let retVal = '';
    // always show description first.
    if (coating.description && coating.description.trim().length > 0) {
      retVal += coating.description + ' ';
    }

    if ((coating.labServiceId && coating.labServiceId.trim().length > 0) ||
      (coating.codes && coating.codes.join().replace(/,/g, '').trim().length > 0)) {
      retVal += '[';
    }

    let idSeparatorNeeded = false;

    if (coating.labServiceId && coating.labServiceId.trim().length > 0) {
      retVal += coating.labServiceId;
      idSeparatorNeeded = true;
    }

    if (coating.codes) {
      coating.codes.forEach(labServiceCode => {
        if (labServiceCode.trim().length > 0) {
          if (idSeparatorNeeded) {
            retVal += ', ';
          }
          retVal += labServiceCode;
          idSeparatorNeeded = true;
        }
      });
    }

    if ((coating.labServiceId && coating.labServiceId.trim().length > 0) ||
      (coating.codes && coating.codes.join().replace(/,/g, '').trim().length > 0)) {
      retVal += ']';
    }

    return retVal;
  }

  getTintColorDesc(lens: Lens): string {
    const code = lens.tintColorCode;
    const description = lens.tintColorDescription;
    if (!code) {
      return '';
    }
    if (code.toLowerCase().indexOf('other') > -1 && description) {
      return description;
    }
    let desc = code;
    this.getTintColorValues().forEach(coating => {
      if (coating.code.toLowerCase() === code.toLowerCase()) {
        desc = coating.labServiceDescription;
      }
    });
    return desc;
  }

  getTintTypeDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getTintTypeValues().forEach(tintType => {
      if (tintType.labOrderApiEnum.toLowerCase() === code.toLowerCase()) {
        desc = tintType.labServiceDescription;
      }
    });
    return desc;
  }

  getBevelDesc(lens: Lens): string {
    const code = lens.bevelCode;
    const description = lens.bevelDescription;
    if (!code) {
      return '';
    }
    if (code.toLowerCase().indexOf('other') > -1 && description) {
      return description;
    }
    let desc = code;
    this.getBevelValues().forEach(bevel => {
      if (bevel.labOrderApiEnum.toLowerCase() === code.toLowerCase()) {
        desc = bevel.labServiceDescription;
      }
    });
    return desc;
  }

  getEdgeDesc(lens: Lens): string {
    const code = lens.edgeCode;
    const description = lens.edgeDescription;
    if (!code) {
      return '';
    }
    if (code.toLowerCase().indexOf('other') > -1 && description) {
      return description;
    }
    let desc = code;
    this.getEdgeValues().forEach(edge => {
      if (edge.labOrderApiEnum.toLowerCase() === code.toLowerCase()) {
        desc = edge.labServiceDescription;
      }
    });
    return desc;
  }

  getTintSampleDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getTintSampleValues().forEach(tintSample => {
      if (tintSample.labOrderApiEnum.toLowerCase() === code.toLowerCase()) {
        desc = tintSample.description;
      }
    });
    return desc;
  }

  getWhichLensRxDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getWhichLensRxValues().forEach(whichLensRx => {
      if (whichLensRx.labOrderApiEnum.toLowerCase() === code.toLowerCase()) {
        desc = whichLensRx.description;
      }
    });
    return desc;
  }

  getWhichLensBalanceDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getWhichLensBalanceValues().forEach(whichLensBal => {
      if (whichLensBal.labOrderApiEnum.toLowerCase() === code.toLowerCase()) {
        desc = whichLensBal.description;
      }
    });
    return desc;
  }

  getVisionTypeDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getVisionTypeValues().forEach(visionType => {
      if (visionType.id.toLowerCase() === code.toLowerCase()) {
        desc = visionType.lensCatalogCode;
      }
    });
    return desc;
  }

  getMaterialTypeDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getMaterialTypeValues().forEach(materialType => {
      if (materialType.id.toLowerCase() === code.toLowerCase()) {
        desc = materialType.lensCatalogCode;
      }
    });
    return desc;
  }

  getOrderStatusDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getOrderStatusValues().forEach(orderStatus => {
      if (orderStatus.status.toLowerCase() === code.toLowerCase()) {
        desc = orderStatus.description;
      }
    });
    return desc;
  }

  getSpecialInstructionTypeDesc(code: string): string {
    if (!code) {
      return '';
    }
    let desc = code;
    this.getSpecialInstructionTypeValues().forEach(specialInstructionType => {
      if (specialInstructionType.id === code) {
        desc = specialInstructionType.name;
      }
    });
    return desc;
  }

  getLensDescription(labOrder: LabOrder) {
    this.lensCatalogBusinessService.getLenses(labOrder)
      .subscribe(
        (data) => {
          console.log('lenses loaded...', data);
          data.forEach(lens => {
            if (lens.externalId === labOrder.lens.spectacleLensId) {
              labOrder.lens.spectacleLensDescription =
                this.getDisplayableLensStyleDescription(labOrder.lens.spectacleLensDescription, lens.productDescription);
            }
          });
        },
        (error) => {
          console.warn('Could not retrieve lenses from the catalog API');
          return '';
        }
      );
  }

  getDisplayableLensStyleDescription(nonFormularyDescription, productDescription) {
    const labOrderLensDesc = nonFormularyDescription ? nonFormularyDescription : '';
    const lCatLensDesc = productDescription ? productDescription : '';
    let lensStyle = '';
    if (labOrderLensDesc && !lCatLensDesc) {
      lensStyle = labOrderLensDesc;
    } else if (!labOrderLensDesc && lCatLensDesc) {
      lensStyle = lCatLensDesc;
    }else if (labOrderLensDesc && lCatLensDesc) {
      lensStyle = lCatLensDesc + ', ' + labOrderLensDesc;
    }
    return lensStyle;
  }

  getThicknessDesc(code: string): string {
    if (!code) {
      return '';
    }

    let retVal = '';
    switch (code) {
      case 'HIGH_IMPACT' :
        retVal = 'High Impact';
        break;
      case 'BASIC_IMPACT' :
        retVal = 'Basic Impact';
        break;
      case 'NON_INDUSTRIAL' :
        retVal = 'Non Industrial';
        break;
      default:
        retVal = 'Unknown Value';
    }
    return retVal;
  }

  private getTintColorValues() {
    return [
      {
        'labServiceId': 'BROW',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Brown 1',
        code: 'Brown_1'
      },
      {
        'labServiceId': 'BROW2',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Brown 2',
        code: 'Brown_2'
      },
      {
        'labServiceId': 'BROW3',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Brown 3',
        code: 'Brown_3'
      },
      {
        'labServiceId': 'CRUXA',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Crux A',
        code: 'Crux_A'
      },
      {
        'labServiceId': 'CRUXX',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Crux AX',
        code: 'Crux_Ax'
      },
      {
        'labServiceId': 'G15',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'G 15',
        code: 'G15'
      },
      {
        'labServiceId': 'GRA1',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Gray 1',
        code: 'Gray_1'
      },
      {
        'labServiceId': 'GRA2',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Gray 2',
        code: 'Gray_2'
      },
      {
        'labServiceId': 'GRA3',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Gray 3',
        code: 'Gray_3'
      },
      {
        'labServiceId': 'OTHER',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Other - Dye not listed',
        code: 'OTHER'
      },
      {
        'labServiceId': 'PINK1',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Pink 1',
        code: 'Pink_1'
      },
      {
        'labServiceId': 'PINK2',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Pink 2',
        code: 'Pink_2'
      },
      {
        'labServiceId': 'PINK3',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Pink 3',
        code: 'Pink_3'
      },
      {
        'labServiceId': 'ROSE1',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Rose 1',
        code: 'Rose_1'
      },
      {
        'labServiceId': 'ROSE2',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Rose 2',
        code: 'Rose_2'
      },
      {
        'labServiceId': 'ROSE3',
        'labServiceType': 'Tint Color',
        'labServiceDescription': 'Rose 3',
        code: 'Rose_3'
      }
    ];
  }

  private getTintTypeValues() {
    return [
      {
        'labOrderApiEnum': 'DR_TINT_IN_OFFICE_SOLID_NOT_PINK',
        'labServiceId': 'T5',
        'labServiceType': 'Tint Type',
        'labServiceDescription': 'Dr Tint in-office - Solid (except Pink I & II)',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.497',
        'createTimestamp': '2019-03-19T11:11:29.497',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'IN'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'PLASTIC_DYES_SOLID_PINK',
        'labServiceId': 'T1',
        'labServiceType': 'Tint Type',
        'labServiceDescription': 'Plastic Dyes - Solid Pink I & II',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.467',
        'createTimestamp': '2019-03-19T11:11:29.467',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'MM'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'PLASTIC_DYES_SOLID_NOT_PINK',
        'labServiceId': 'T2',
        'labServiceType': 'Tint Type',
        'labServiceDescription': 'Plastic Dyes - Solid (except Pink I & II)',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.480',
        'createTimestamp': '2019-03-19T11:11:29.480',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'MN'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'PLASTIC_DYES_GRADIENT_COLOR',
        'labServiceId': 'T3',
        'labServiceType': 'Tint Type',
        'labServiceDescription': 'Plastic Dyes - Gradient Color',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.480',
        'createTimestamp': '2019-03-19T11:11:29.480',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'MP'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'DR_TINT_IN_OFFICE_SOLID_PINK',
        'labServiceId': 'T4',
        'labServiceType': 'Tint Type',
        'labServiceDescription': 'Dr Tint in-office - Solid Pink I & II',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.480',
        'createTimestamp': '2019-03-19T11:11:29.480',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'IM'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'DR_TINT_IN_OFFICE_GRADIENT_COLOR',
        'labServiceId': 'T6',
        'labServiceType': 'Tint Type',
        'labServiceDescription': 'Dr Tint in-office - Gradient Color',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.497',
        'createTimestamp': '2019-03-19T11:11:29.497',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'IP'
            }
          ]
        }
      }
    ];
  }

  private getTintSampleValues() {
    return [
      {
        'labOrderApiEnum': 'NO_SAMPLE',
        'description': 'No Sample',
      },
      {
        'labOrderApiEnum': 'MATCH_SAMPLE',
        'description': 'Match Sample',
      },
      {
        'labOrderApiEnum': 'TINT_LIGHTER',
        'description': 'Tint Lighter',
      },
      {
        'labOrderApiEnum': 'TINT_DARKER',
        'description': 'Tint Darker',
      }
    ];
  }

  private getEdgeValues() {
    return [
      {
        'labOrderApiEnum': 'EDGE_COATING',
        'labServiceId': 'E4',
        'labServiceType': 'Edge',
        'labServiceDescription': 'Edge Coating',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.370',
        'createTimestamp': '2019-03-19T11:11:29.370',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'SQ'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'ROLL_AND_HIGH_LUSTER_FINISH',
        'labServiceId': 'E2',
        'labServiceType': 'Edge',
        'labServiceDescription': 'Roll and High Luster Polish',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.370',
        'createTimestamp': '2019-03-19T11:11:29.370',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'SP'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'FACETTED_LENSES',
        'labServiceId': 'E5',
        'labServiceType': 'Edge',
        'labServiceDescription': 'Facetted Lenses',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.387',
        'createTimestamp': '2019-03-19T11:11:29.387',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'SR'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'STANDARD_FINISH',
        'labServiceId': 'E6',
        'labServiceType': 'Edge',
        'labServiceDescription': 'Standard Finish',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-06-19T12:00:15.330',
        'createTimestamp': '2019-06-19T12:00:15.330',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {}
          ]
        }
      },
      {
        'labOrderApiEnum': 'ROLL_AND_STANDARD_FINISH',
        'labServiceId': 'E7',
        'labServiceType': 'Edge',
        'labServiceDescription': 'Roll and Standard Finish',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-06-19T12:00:15.360',
        'createTimestamp': '2019-06-19T12:00:15.360',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {}
          ]
        }
      },
      {
        'labOrderApiEnum': 'HIGH_LUSTER_EDGE_POLISH',
        'labServiceId': 'E1',
        'labServiceType': 'Edge',
        'labServiceDescription': 'High Luster Edge Polish',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.370',
        'createTimestamp': '2019-03-19T11:11:29.370',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'SP'
            }
          ]
        }
      },
      {
        'labOrderApiEnum': 'PAINTED_GROOVE',
        'labServiceId': 'E3',
        'labServiceType': 'Edge',
        'labServiceDescription': 'Painted Groove',
        'labServiceEffectiveDate': '1994-06-01',
        'labServiceLimitDate': '9999-12-31',
        'lastUpdateTimestamp': '2019-03-19T11:11:29.370',
        'createTimestamp': '2019-03-19T11:11:29.370',
        'vspFormularyAssessment': {
          'formularyEnhancements': [
            {
              'code': 'SQ'
            }
          ]
        }
      }
    ];
  }

  private getBevelValues() {
    return [
      {
        'labOrderApiEnum': 'ONE_THIRD_TO_TWO_THIRD',
        'labServiceId': '1',
        'labServiceType': 'Bevel',
        'labServiceDescription': '1/3 to 2/3',
        'vspFormularyAssessment': {}
      },
      {
        'labOrderApiEnum': 'FIFTY_FIFTY',
        'labServiceId': '2',
        'labServiceType': 'Bevel',
        'labServiceDescription': '50/50',
        'vspFormularyAssessment': {}
      },
      {
        'labOrderApiEnum': 'LAYOVER',
        'labServiceId': '3',
        'labServiceType': 'Bevel',
        'labServiceDescription': 'Layover',
        'vspFormularyAssessment': {}
      },
      {
        'labOrderApiEnum': 'RIMLESS_OR_FLAT',
        'labServiceId': '4',
        'labServiceType': 'Bevel',
        'labServiceDescription': 'Rimless or Flat',
        'vspFormularyAssessment': {}
      },
      {
        'labOrderApiEnum': 'HIDE_A_BEVEL',
        'labServiceId': '6',
        'labServiceType': 'Bevel',
        'labServiceDescription': 'Hide-a-Bevel',
        'vspFormularyAssessment': {}
      }
    ];
  }

  private getWhichLensRxValues() {
    return [
      {
        'labOrderApiEnum': 'TWO_LENSES',
        'description': 'Two Lenses',
      },
      {
        'labOrderApiEnum': 'NEITHER',
        'description': 'Neither',
      },
      {
        'labOrderApiEnum': 'RIGHT_LENS_ONLY',
        'description': 'Right Lens Only',
      },
      {
        'labOrderApiEnum': 'Left_LENS_ONLY',
        'description': 'Left Lens Only',
      }
    ];
  }

  private getWhichLensBalanceValues() {
    return [
      {
        'labOrderApiEnum': 'NOT_BALANCED',
        'description': 'Not Balanced',
      },
      {
        'labOrderApiEnum': 'BALANCED_RIGHT',
        'description': 'Balanced Right',
      },
      {
        'labOrderApiEnum': 'BALANCED_LEFT',
        'description': 'Balanced Left',
      }
    ];
  }

  private getVisionTypeValues() {
    return [
      {externalId: 0, id: 'SINGLE_VISION', lensCatalogCode: 'Single Vision'},
      {externalId: 1, id: 'BIFOCAL', lensCatalogCode: 'Bifocal'},
      {externalId: 2, id: 'TRIFOCAL', lensCatalogCode: 'Trifocal'},
      {externalId: 3, id: 'QUAD', lensCatalogCode: 'Quad'},
      {externalId: 4, id: 'DOUBLE_VISION', lensCatalogCode: 'Double'},
      {externalId: 5, id: 'PROGRESSIVE', lensCatalogCode: 'Progressive'},
      {externalId: 6, id: 'NEAR_VARIABLE_FOCUS', lensCatalogCode: 'Near Variable Focus'},
      {externalId: 7, id: 'LENTICULAR_SINGLE_VISION', lensCatalogCode: 'Lenticular Single Vision'},
      {externalId: 8, id: 'LENTICULAR_BIFOCAL', lensCatalogCode: 'Lenticular Bifocal'}
    ];
  }

  private getMaterialTypeValues() {
    return [
      {externalId: 0, id: 'GLASS', lensCatalogCode: 'Glass'},
      {externalId: 1, id: 'POLYCARBONATE', lensCatalogCode: 'Polycarbonate'},
      {externalId: 2, id: 'PLASTIC', lensCatalogCode: 'Plastic'},
      {externalId: 3, id: 'PLASTIC_HIGH_INDEX', lensCatalogCode: 'Plastic Hi Index'},
      {externalId: 4, id: 'GLASS_HIGH_INDEX', lensCatalogCode: 'Glass Hi Index'},
      {externalId: 5, id: 'PLASTIC_MID_INDEX', lensCatalogCode: 'Plastic Mid Index'},
      {externalId: 6, id: 'TRIVEX', lensCatalogCode: 'Trivex'}
    ];
  }

  private getOrderStatusValues() {
    return [
      {labOrderStatusCode: '00', status: 'SAVED_00', description: 'Saved'},
      {labOrderStatusCode: '04', status: 'LAB_VOIDED_04', description: 'Lab Voided'},
      {labOrderStatusCode: '05', status: 'SUBMIT_05', description: 'Submitted'},
      {labOrderStatusCode: '21', status: 'LAB_ACCEPTED_21', description: 'Lab Accepted'},
      {labOrderStatusCode: '23', status: 'MODIFIED_23', description: 'Modified'},
      {labOrderStatusCode: '27', status: 'CLAIM_READY_COMPLETED_VSP_ORDER_27', description: 'Completed'},
      {labOrderStatusCode: '98', status: 'COMPLETED_PRIVATE_ORDER_98', description: 'Completed'},
      {labOrderStatusCode: '99', status: 'DELETED_99', description: 'Deleted'}
    ];
  }

  private getSpecialInstructionTypeValues() {
    return [
      {id: '', name: 'None'},
      {id: 'BACKSIDE', name: 'Backside A/R Only'},
      {id: 'REDO', name: 'Redo Order'},
      {id: 'OTHER', name: 'Other'}
    ];
  }

  public getShapeNames(name: string) {
    if (name === 'P_3') {
      return 'P-3';
    } else if (name === 'CORNERED_RECT') {
      return 'Cornered Rect';
    } else if (name === 'TAPERED_OVAL') {
      return 'Tapered Oval';
    } else {
      return UtilsService.lowerCaseAllWordsExceptFirstLetters(name);
    }
  }

  getLensEnhancementDescriptions(lensEnhancementDescriptionsMap: Map<string, LensEnhancementDescription>,
                                 billingOptionCodes: BillingOptionCode[]): string {
    let desc = '';
    if (billingOptionCodes) {
      billingOptionCodes.forEach(billingCode => {
        const lensEnhancementDescription = lensEnhancementDescriptionsMap.get(billingCode.code);
        if (lensEnhancementDescription) {
          desc += lensEnhancementDescription.code + ' - ' + lensEnhancementDescription.description + '; ';
        } else {
          desc += billingCode.code + '; ';
        }
      });
    }
    return desc;
  }
}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-sandbox-build/src/app/lab-code-mapper/lab-code-mapper.service.ts